import { useEffect, useMemo, useState } from "react";
import { getSessionToken } from "../../hooks/useSession";
import { AgentDetailData, API_BASE, ProviderContract } from "./types";

export function useAgentDetail(managedAgentId?: string) {
  const token = useMemo(() => getSessionToken(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<AgentDetailData | null>(null);
  const [contracts, setContracts] = useState<ProviderContract[]>([]);

  async function reload() {
    if (!token || !managedAgentId) return;
    setLoading(true);
    setError(null);
    try {
      const [userRes, contractsRes] = await Promise.all([
        fetch(`${API_BASE}/api/agents/${managedAgentId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/agents/${managedAgentId}/provider-contracts`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [userJson, contractsJson] = await Promise.all([userRes.json(), contractsRes.json()]);
      if (!userRes.ok || !userJson.success) throw new Error(userJson.error || "Failed to load agent");
      if (!contractsRes.ok || !contractsJson.success) throw new Error(contractsJson.error || "Failed to load contracts");
      setUser(userJson.data as AgentDetailData);
      setContracts(contractsJson.data as ProviderContract[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, managedAgentId]);

  function patchUser(patch: Partial<AgentDetailData>) {
    setUser((current) => (current ? { ...current, ...patch } : current));
  }

  return { token, loading, error, setError, user, contracts, reload, patchUser };
}
